import React, { useCallback, useMemo, useRef, useState } from 'react';
import Input from '../../input';
import { Dropdown } from '../../dropdown';
import { Icon } from '../../../mobx/interfaces/project';

interface DropdownSearchProps<T extends { id: string; name: string; profile_photo?: string; icon?: Icon | null }> {
  isMinWidth?: number;
  modalTitle?: string;
  // NewProps added for component
  selectedOptions: T[];
  onRemove: (item: T) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onEnterOrComma?: () => void;
  placeholder?: string;
  children?: React.ReactNode;
  onChangeSearch?: (value: string) => void;
}

const DropdownSearch = <T extends { id: string; name: string; profile_photo?: string; icon?: Icon | null }>({
  children,
  isMinWidth,
  selectedOptions = [],
  placeholder,
  searchValue,
  onSearchChange,
  onRemove,
  onEnterOrComma,
  onChangeSearch,
  ...dropdownProps
}: DropdownSearchProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<{
    closeDropdown: () => void;
  }>(null);

  // Handler for key events in the project search input
  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && searchValue === '' && selectedOptions.length > 0) {
        // On click backspace
        if (onRemove) onRemove(selectedOptions[selectedOptions?.length - 1]);
      }
      if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && searchValue.trim() !== '') {
        e.preventDefault();
        // On enter or comma when search something
        if (onEnterOrComma) onEnterOrComma();
      }
    },
    [onRemove, onEnterOrComma, searchValue, selectedOptions]
  );

  const handleIconClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      if (!isDropdownOpen) return;
      e.preventDefault();
      e.stopPropagation();
      dropdownRef?.current?.closeDropdown();
    },
    [isDropdownOpen]
  );

  const showPlaceHolder =
    selectedOptions.length === 0 && searchValue === '' ? (placeholder !== undefined ? placeholder : 'Select...') : '';

  const inputContent = useMemo(() => {
    return (
      <Input
        inputType='tags'
        multiSelectProps={{
          isActive: isDropdownOpen,
          selectedUsers: selectedOptions.map((user) => ({
            id: user.id,
            displayName: user.name,
            profile_photo: user.profile_photo,
            projectIcon: user.icon
          })),
          value: searchValue,
          onChange: (e) => onSearchChange && onSearchChange(e.target.value),
          onKeyDown: onInputKeyDown,
          inputRef: inputRef,
          showIcon: true,
          isDropdownOpen,
          onIconClick: handleIconClick,
          onRemoveUser: (user) => onRemove(user as unknown as T),
          placeholderWhenEmpty: showPlaceHolder
        }}
      />
    );
  }, [
    isDropdownOpen,
    selectedOptions,
    searchValue,
    onInputKeyDown,
    showPlaceHolder,
    onSearchChange,
    onRemove,
    handleIconClick
  ]);

  return (
    <Dropdown
      {...dropdownProps}
      content={inputContent}
      closeOnItemClick={false}
      ref={dropdownRef}
      isMinWidth={isMinWidth}
      updateDropdownOpen={setIsDropdownOpen}
      onChangeSearch={onChangeSearch}>
      {children}
    </Dropdown>
  );
};

export default DropdownSearch;
